import { selectStandardProps } from "@/utils/componentUtils";
import { Select } from "antd";
import { useMemo } from "react";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { AgentDirectorateBase } from "../../../agent/types";
import { formatAgentDirectorateName } from "../../../agent/utils";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { EnumerationType } from "../../enums";
import { filterSubordinateAgentDirectorates } from "../../utils";

type Props<T extends boolean> = {
  value?: T extends true ? UUID[] : UUID;
  allowClear?: boolean;
  isMultiple?: T;
  dataSource?: AgentDirectorateBase[];
  showAllDirectorates?: boolean;
  onChange?: (data: T extends true ? UUID[] : UUID) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
};

export const AgentDirectoratesSelect = <T extends boolean>({
  value,
  allowClear,
  isMultiple,
  dataSource,
  showAllDirectorates,
  onChange,
  disabled,
  style,
}: Props<T>) => {
  const directoratesEnums = useEnumeration(EnumerationType.AGENT_DIRECTORATES);
  const currentAgent = useCurrentAgentAccount()?.agent;

  const data = useMemo<AgentDirectorateBase[]>(() => {
    if (dataSource) {
      return dataSource;
    }
    if (showAllDirectorates) {
      return directoratesEnums;
    }

    return filterSubordinateAgentDirectorates(directoratesEnums, currentAgent, value);
  }, [dataSource, showAllDirectorates, directoratesEnums, currentAgent, value]);

  return (
    <Select
      style={style}
      {...selectStandardProps}
      allowClear={allowClear}
      maxTagCount="responsive"
      mode={isMultiple ? "multiple" : undefined}
      options={data.map((directorate) => ({
        label: formatAgentDirectorateName(directorate),
        value: directorate.id,
      }))}
      onChange={onChange}
      value={value}
      disabled={disabled}
    />
  );
};
